import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Place } from 'infra/type'
import { ColumnLayout, RowLayout } from 'ui/common/layout'
import styled from 'styled-components'
import { colors } from 'infra/color'
import { Body2, H2 } from 'ui/common/text'
import { TouchableOpacity, View } from 'react-native'
import { navigation } from 'navigation/global'
import { PreferenceButtonArea } from 'ui/common/preference-button-area.tsx'
import { categoryStore } from 'store/category'
import { alert } from 'infra/util'
import { MapStore } from 'store/map'
import { observer } from 'mobx-react'
import { WebView } from 'react-native-webview'
import { Loading } from 'ui/common/loading'
import { requestDeletePlace } from 'api/writes'
import { BOTTOM_VIEW_BOTTOM_DISTANCE, CURRENT_OS, OS } from 'infra/constant'
import { isolateNaverPlacePageInjectScript } from 'ui/common/isolate-naver-place-page'

export const PlaceCard: React.FC<{
  data: Place
}> = ({ data }) => {
  const goToPlaceDetailScreen = useCallback(() => {
    const priorities = ['naver', 'kakao', 'google']
    const provider = priorities.find((priority) =>
      data.references.find((x) => x.provider === priority),
    )
    if (provider) {
      navigation.navigate('PlaceDetailScreen', { place: data, provider })
    }
  }, [data])
  const webViewRef = useRef<WebView>(null)
  const [loading, setLoading] = useState(true)
  const onMessage = useCallback(
    (e: any) => {
      const msg = e?.nativeEvent?.data
      if (__DEV__ && typeof msg === 'string' && msg.startsWith('isolate:')) {
        console.log('[PlaceCard]', msg)
      }
      // 상단 이미지 헤더 누른 경우
      if (msg === 'clicked') {
        goToPlaceDetailScreen()
        return
      }
      if (msg === 'done') {
        // isolate 직후 한 프레임 여유 (구 500ms → 120ms)
        setTimeout(() => setLoading(false), 120)
        return
      }
      if (msg === 'page_deleted') {
        alert(
          '그새 장소가 없어진 것 같아요 🥹',
          '불편을 드려 죄송해요 🙏\n매니저가 확인해보고 장소를 삭제할게요.',
        )
        requestDeletePlace(data.id)
        return
      }
    },
    [goToPlaceDetailScreen, data.id],
  )
  const naverUrl = data.references.find((x) => x.provider === 'naver')?.link
  useEffect(() => {
    setLoading(true)
  }, [naverUrl])
  return (
    <Container
      style={{
        shadowColor: '#000000',
        shadowOffset: { width: 2, height: 3 },
        shadowOpacity: 0.25,
        shadowRadius: 6,
        elevation: 6,
      }}
    >
      <RowLayout>
        <TouchableOpacity
          style={{
            flex: 1,
            paddingLeft: 16,
            paddingRight: 4,
            paddingVertical: 12,
          }}
          onPress={() => {
            goToPlaceDetailScreen()
          }}
        >
          <H2
            style={{
              fontWeight: 'bold',
              color: colors.primary,
              marginBottom: 2,
            }}
          >
            {data.name}
          </H2>
          <Body2 style={{ color: colors.gray.v700 }}>
            {categoryStore.formatCategoryIds(data.categories)}
          </Body2>
        </TouchableOpacity>
        <RowLayout style={{ paddingRight: 12, paddingLeft: 4 }}>
          <PreferenceButtonArea data={data} />
        </RowLayout>
      </RowLayout>
      {/* 웹은 cross-origin iframe 에 스크립트 주입/ postMessage 가 불가능해
          m.place 사진·영업시간 추출을 못 한다. 웹에서는 이 블록을 생략하고
          이름·카테고리·선호 카드만 보여준다. (배포 정책 b)
          디버그는 pnpm web:place-card-naver-map-iframe (프록시 + 직접 JS 호출). */}
      {!!naverUrl && CURRENT_OS !== OS.WEB && (
        <View style={{ height: 218 }}>
          {/* WebView 는 항상 풀폭으로 렌더돼야 내부 레이아웃이 처리된다.
              로딩 동안엔 opacity 0 으로 가렸다가 보정이 끝난 뒤(done+딜레이) 연다.
              BeforeContentLoaded 로 페이지 로드와 병렬로 돌리고,
              스크립트 안에서 스크롤로 lazy 영업시간만 확보한다. */}
          <WebView
            ref={webViewRef}
            source={{ uri: naverUrl }}
            style={{ height: 218, opacity: loading ? 0 : 1 }}
            injectedJavaScriptBeforeContentLoaded={
              isolateNaverPlacePageInjectScript
            }
            onMessage={onMessage}
            nestedScrollEnabled
            setSupportMultipleWindows={false}
          />
          {loading && (
            <View
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                justifyContent: 'center',
              }}
            >
              <Loading />
            </View>
          )}
        </View>
      )}
    </Container>
  )
}

const PlacePopover: React.FC<{
  data: Place
}> = ({ data }) => {
  return (
    <BottomPlacePopover style={{ bottom: BOTTOM_VIEW_BOTTOM_DISTANCE }}>
      <PlaceCard data={data} />
    </BottomPlacePopover>
  )
}

const Container = styled(ColumnLayout)`
  border-radius: 12px;
  background-color: ${colors.white};
  overflow: hidden;
`

const BottomPlacePopover = styled(View)`
  position: absolute;
  width: 100%;
  padding: 0 20px;
`

export const SelectedPlacePopover: React.FC<{
  store: MapStore
}> = observer(({ store }) => {
  const place = store.selectedPlace
  if (!place) return null
  return <PlacePopover data={place} />
})
